import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowUpTrayIcon,
  BookOpenIcon,
  ArrowTrendingUpIcon,
  ArrowTrendingDownIcon,
  ClockIcon,
  CheckCircleIcon,
  ExclamationTriangleIcon,
  PlayIcon,
  CpuChipIcon,
} from '@heroicons/react/24/outline';
import Card, { CardHeader, CardBody } from '@/components/ui/Card';
import Button from '@/components/ui/Button';
import Badge from '@/components/ui/Badge';
import Avatar, { AvatarGroup } from '@/components/ui/Avatar';
import LoadingOverlay, { WidgetWrapper } from '@/components/ui/LoadingOverlay';
import { apiClient } from '@/lib/api';
import {
  AreaChart,
  Area,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';
import type { CombinedStats, TTSJob } from '@/types/api';
import { getJobTitle, getJobVoice } from '@/types/api';

// Fallback mock data for widgets not connected
const recentBooks = [
  { id: 1, isbn: '978-2-123-456-7', title: 'Le Petit Prince', publisher: 'Éditions Kampus', status: 'PENDING' },
  { id: 2, isbn: '978-2-789-012-3', title: 'Guide voyage Paris', publisher: 'Voyage Plus', status: 'PUBLISHED' },
  { id: 3, isbn: '978-2-345-678-9', title: 'Manuel technique', publisher: 'TechLib', status: 'FAILED' },
  { id: 4, isbn: '978-2-901-234-5', title: 'Recettes françaises', publisher: 'Cuisine Ed.', status: 'PUBLISHED' },
  { id: 5, isbn: '978-2-567-890-1', title: 'Poésie moderne', publisher: 'Éditions Kampus', status: 'PENDING' },
];

// Mock activity data - not connected yet
const activityData = [
  { name: 'Mon', jobs: 12, books: 8 },
  { name: 'Tue', jobs: 19, books: 12 },
  { name: 'Wed', jobs: 15, books: 10 },
  { name: 'Thu', jobs: 22, books: 15 },
  { name: 'Fri', jobs: 28, books: 18 },
  { name: 'Sat', jobs: 8, books: 5 },
  { name: 'Sun', jobs: 5, books: 3 },
];

const getStatusBadge = (status: string) => {
  switch (status) {
    case 'completed':
    case 'PUBLISHED':
      return <Badge variant="success" dot>Completed</Badge>;
    case 'running':
    case 'processing':
    case 'PROCESSING':
      return <Badge variant="primary" dot>Running</Badge>;
    case 'pending':
    case 'PENDING':
      return <Badge variant="warning" dot>Pending</Badge>;
    case 'failed':
    case 'FAILED':
      return <Badge variant="danger" dot>Failed</Badge>;
    default:
      return <Badge variant="slate">{status}</Badge>;
  }
};

// Pipeline Card Component - Kampus Media Brand Style
interface PipelineCardProps {
  title: string;
  value: number;
  change: number;
  changeLabel: string;
  icon: React.ReactNode;
  color: 'orange' | 'purple' | 'emerald' | 'rose';
  progress?: number;
  isLoading?: boolean;
}

function PipelineCard({ title, value, change, changeLabel, icon, color, progress, isLoading }: PipelineCardProps) {
  const colorClasses = {
    orange: {
      bg: 'from-primary-500 to-orange-600',
      glow: 'shadow-primary-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    purple: {
      bg: 'from-secondary-500 to-violet-600',
      glow: 'shadow-secondary-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    emerald: {
      bg: 'from-emerald-500 to-teal-600',
      glow: 'shadow-emerald-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
    rose: {
      bg: 'from-rose-500 to-pink-600',
      glow: 'shadow-rose-500/25',
      icon: 'bg-white/20 text-white',
      bar: 'bg-white/60',
    },
  };

  const TrendIcon = change < 0 ? ArrowTrendingDownIcon : ArrowTrendingUpIcon;

  return (
    <div
      className={`relative overflow-hidden rounded-2xl bg-gradient-to-br ${colorClasses[color].bg} p-6 shadow-xl ${colorClasses[color].glow} transition-all duration-300 hover:scale-[1.02] hover:shadow-2xl ${isLoading ? 'opacity-40 animate-pulse' : ''}`}
    >
      {/* Background pattern */}
      <div className="absolute inset-0 opacity-10">
        <svg className="w-full h-full" viewBox="0 0 100 100" preserveAspectRatio="none">
          <defs>
            <pattern id={`grid-${color}`} width="10" height="10" patternUnits="userSpaceOnUse">
              <circle cx="1" cy="1" r="1" fill="white" />
            </pattern>
          </defs>
          <rect width="100" height="100" fill={`url(#grid-${color})`} />
        </svg>
      </div>

      {/* Decorative circles */}
      <div className="absolute -right-4 -top-4 w-24 h-24 rounded-full bg-white/10 blur-xl" />
      <div className="absolute -right-8 -bottom-8 w-32 h-32 rounded-full bg-white/5" />

      <div className="relative z-10">
        <div className="flex items-start justify-between mb-4">
          <div className={`p-3 rounded-xl ${colorClasses[color].icon}`}>
            {icon}
          </div>
          <div className="flex items-center gap-1 px-2 py-1 rounded-full bg-white/20 backdrop-blur-sm">
            <TrendIcon className="w-3.5 h-3.5 text-white" />
            <span className="text-xs font-semibold text-white">{Math.abs(change)}%</span>
          </div>
        </div>

        <p className="text-white/80 text-sm font-medium mb-1">{title}</p>
        <p className="text-4xl font-bold text-white mb-2 tracking-tight">{value.toLocaleString()}</p>
        <p className="text-white/60 text-xs">{changeLabel}</p>

        {/* Progress bar for running jobs */}
        {progress !== undefined && (
          <div className="mt-4">
            <div className="h-1.5 bg-white/20 rounded-full overflow-hidden">
              <div
                className={`h-full ${colorClasses[color].bar} rounded-full transition-all duration-500`}
                style={{ width: `${progress}%` }}
              />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default function DashboardPage() {
  // Real data state
  const [stats, setStats] = useState<CombinedStats | null>(null);
  const [recentJobs, setRecentJobs] = useState<TTSJob[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [jobsLoading, setJobsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [jobsError, setJobsError] = useState<string | null>(null);

  // Calculate running progress (average of running jobs)
  const runningProgress = recentJobs
    .filter(j => j.status === 'running' || j.status === 'processing')
    .reduce((sum, j) => sum + j.progress, 0) / Math.max(1, recentJobs.filter(j => j.status === 'running' || j.status === 'processing').length) || 0;

  // Fetch combined stats from backend
  const fetchStats = useCallback(async () => {
    try {
      setStatsLoading(true);
      const response = await apiClient.get<CombinedStats>('tts/queue/stats');
      setStats(response);
      setStatsError(null);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      setStatsError(err instanceof Error ? err.message : 'Failed to fetch stats');
    } finally {
      setStatsLoading(false);
    }
  }, []);

  // Fetch recent jobs from backend
  const fetchJobs = useCallback(async () => {
    try {
      setJobsLoading(true);
      const response = await apiClient.get<TTSJob[]>('tts/jobs?limit=5');
      setRecentJobs(response);
      setJobsError(null);
    } catch (err) {
      console.error('Failed to fetch jobs:', err);
      setJobsError(err instanceof Error ? err.message : 'Failed to fetch jobs');
    } finally {
      setJobsLoading(false);
    }
  }, []);

  // Initial data fetch
  useEffect(() => {
    fetchStats();
    fetchJobs();
  }, [fetchStats, fetchJobs]);

  // Poll for updates every 10 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      fetchStats();
      fetchJobs();
    }, 10000);
    return () => clearInterval(interval);
  }, [fetchStats, fetchJobs]);

  // Extract stats values with fallbacks
  const pendingCount = stats?.queue_stats.pending_jobs ?? 0;
  const runningCount = stats?.queue_stats.processing_jobs ?? 0;
  const completedCount = stats?.job_metrics.completed_jobs ?? 0;
  const failedCount = stats?.job_metrics.failed_jobs ?? 0;
  const successRate = stats?.job_metrics.success_rate ?? 0;

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Page Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 dark:text-white">Dashboard</h1>
          <p className="text-slate-500 dark:text-slate-400 mt-1">Welcome back! Here's an overview of your pipelines.</p>
        </div>
        <div className="flex gap-3">
          <Link to="/tts/upload">
            <Button
              leftIcon={<ArrowUpTrayIcon className="w-4 h-4" />}
              className="bg-primary-500 hover:bg-primary-600 focus:ring-primary-500"
            >
              New Upload
            </Button>
          </Link>
          <Link to="/distribution/books">
            <Button variant="outline" leftIcon={<BookOpenIcon className="w-4 h-4" />}>
              Manage Books
            </Button>
          </Link>
        </div>
      </div>

      {/* TTS Pipeline Section - Connected to Real Backend */}
      <section>
        <div className="flex items-center gap-3 mb-6">
          <h2 className="text-lg font-semibold text-slate-800 dark:text-white">TTS Pipeline</h2>
          <Badge variant="primary" className="animate-pulse-soft">Live</Badge>
          {statsError && (
            <span className="text-xs text-red-500">Connection Error</span>
          )}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <PipelineCard
            title="Pending Jobs"
            value={pendingCount}
            change={0}
            changeLabel="in queue"
            icon={<ClockIcon className="w-6 h-6" />}
            color="orange"
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Running"
            value={runningCount}
            change={0}
            changeLabel="processing now"
            icon={<PlayIcon className="w-6 h-6" />}
            color="purple"
            progress={runningProgress || 50}
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Completed"
            value={completedCount}
            change={Math.round(successRate)}
            changeLabel="success rate"
            icon={<CheckCircleIcon className="w-6 h-6" />}
            color="emerald"
            isLoading={statsLoading}
          />
          <PipelineCard
            title="Failed"
            value={failedCount}
            change={0}
            changeLabel="needs attention"
            icon={<ExclamationTriangleIcon className="w-6 h-6" />}
            color="rose"
            isLoading={statsLoading}
          />
        </div>
      </section>

      {/* Main Content Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Activity Chart - NOT CONNECTED (20% opacity) */}
        <WidgetWrapper isConnected={false} className="lg:col-span-2">
          <Card>
            <CardHeader title="Weekly Activity" subtitle="Jobs processed and books published" />
            <CardBody>
              <div className="h-72">
                <ResponsiveContainer width="100%" height="100%">
                  <AreaChart data={activityData}>
                    <defs>
                      <linearGradient id="colorJobs" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#F58220" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#F58220" stopOpacity={0} />
                      </linearGradient>
                      <linearGradient id="colorBooks" x1="0" y1="0" x2="0" y2="1">
                        <stop offset="5%" stopColor="#8E7CC3" stopOpacity={0.3} />
                        <stop offset="95%" stopColor="#8E7CC3" stopOpacity={0} />
                      </linearGradient>
                    </defs>
                    <CartesianGrid strokeDasharray="3 3" stroke="#E2E8F0" />
                    <XAxis dataKey="name" stroke="#94A3B8" fontSize={12} />
                    <YAxis stroke="#94A3B8" fontSize={12} />
                    <Tooltip
                      contentStyle={{
                        backgroundColor: 'white',
                        border: '1px solid #E2E8F0',
                        borderRadius: '12px',
                        boxShadow: '0 4px 6px -1px rgba(0,0,0,0.1)',
                      }}
                    />
                    <Area
                      type="monotone"
                      dataKey="jobs"
                      stroke="#F58220"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorJobs)"
                      name="TTS Jobs"
                    />
                    <Area
                      type="monotone"
                      dataKey="books"
                      stroke="#8E7CC3"
                      strokeWidth={2}
                      fillOpacity={1}
                      fill="url(#colorBooks)"
                      name="Books"
                    />
                  </AreaChart>
                </ResponsiveContainer>
              </div>
            </CardBody>
          </Card>
        </WidgetWrapper>

        {/* Active Users - NOT CONNECTED (20% opacity) */}
        <WidgetWrapper isConnected={false}>
          <Card>
            <CardHeader title="Active Users" subtitle="Currently online" />
            <CardBody>
              <div className="text-center mb-6">
                <p className="text-4xl font-bold text-red-500 opacity-50">--</p>
                <p className="text-slate-500 dark:text-slate-400 mt-1">Page Views per Minutes</p>
              </div>

              <div className="mb-6">
                <p className="text-sm text-slate-500 dark:text-slate-400 mb-2">Today Users</p>
                <AvatarGroup max={5}>
                  <Avatar name="John Doe" size="md" />
                  <Avatar name="Jane Smith" size="md" />
                  <Avatar name="Mike Johnson" size="md" />
                  <Avatar name="Sarah Williams" size="md" />
                  <Avatar name="Chris Brown" size="md" />
                  <Avatar name="Emily Davis" size="md" />
                  <Avatar name="David Wilson" size="md" />
                </AvatarGroup>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-red-500 opacity-50">--</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Monthly</p>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-red-500 opacity-50">--</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Weekly</p>
                </div>
                <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-xl">
                  <p className="text-lg font-bold text-red-500 opacity-50">--</p>
                  <p className="text-xs text-slate-500 dark:text-slate-400">Yearly</p>
                </div>
              </div>

              <Link to="/tts/telemetry">
                <Button className="w-full mt-6" variant="primary">
                  Details
                </Button>
              </Link>
            </CardBody>
          </Card>
        </WidgetWrapper>
      </div>

      {/* Recent Activity Tables */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Recent TTS Jobs - CONNECTED */}
        <LoadingOverlay isLoading={jobsLoading} loadingOpacity={40}>
          <Card>
            <CardHeader
              title="Recent Jobs"
              subtitle="Latest TTS conversions"
              action={
                <Link to="/tts/jobs">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Job</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Voice</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentJobs.length === 0 && !jobsLoading ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-8 text-center text-slate-500">
                        {jobsError ? (
                          <span className="text-red-500">{jobsError}</span>
                        ) : (
                          'No recent jobs'
                        )}
                      </td>
                    </tr>
                  ) : (
                    recentJobs.slice(0, 5).map((job) => (
                      <tr key={job.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                        <td className="px-4 py-3">
                          <Link to={`/tts/jobs/${job.job_id}`} className="hover:text-primary-500">
                            <p className="font-medium text-slate-800 dark:text-white">{getJobTitle(job)}</p>
                            <p className="text-xs text-slate-400">{job.job_id}</p>
                          </Link>
                        </td>
                        <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">
                          {getJobVoice(job)}
                        </td>
                        <td className="px-4 py-3">{getStatusBadge(job.status)}</td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          </Card>
        </LoadingOverlay>

        {/* Recent Books - NOT CONNECTED (20% opacity) */}
        <WidgetWrapper isConnected={false}>
          <Card>
            <CardHeader
              title="Recent Books"
              subtitle="Latest distribution activity"
              action={
                <Link to="/distribution/books">
                  <Button variant="ghost" size="sm">View All</Button>
                </Link>
              }
            />
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-slate-50 dark:bg-slate-800">
                  <tr>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Book</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Publisher</th>
                    <th className="px-4 py-3 text-left text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-100 dark:divide-slate-800">
                  {recentBooks.map((book) => (
                    <tr key={book.id} className="hover:bg-slate-50 dark:hover:bg-slate-800 transition-colors">
                      <td className="px-4 py-3">
                        <Link to={`/distribution/books/${book.id}`} className="hover:text-primary-500">
                          <p className="font-medium text-slate-800 dark:text-white">{book.title}</p>
                          <p className="text-xs text-slate-400">{book.isbn}</p>
                        </Link>
                      </td>
                      <td className="px-4 py-3 text-sm text-slate-600 dark:text-slate-300">{book.publisher}</td>
                      <td className="px-4 py-3">{getStatusBadge(book.status)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        </WidgetWrapper>
      </div>

      {/* System Health - Connected to backend status */}
      <div className="relative mt-8">
        {/* Decorative separator */}
        <div className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-slate-300 dark:via-slate-700 to-transparent" />

        <div className="pt-6">
          <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 px-4 py-3 rounded-xl bg-slate-100/50 dark:bg-slate-800/50 backdrop-blur-sm border border-slate-200/50 dark:border-slate-700/50">
            <div className="flex items-center gap-2">
              <CpuChipIcon className="w-4 h-4 text-slate-400" />
              <span className="text-xs font-medium text-slate-500 dark:text-slate-400 uppercase tracking-wider">System Status</span>
            </div>

            <div className="flex flex-wrap items-center gap-4 sm:gap-6">
              {/* TTS Worker */}
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${stats?.system_status === 'healthy' ? 'bg-emerald-500 animate-pulse' : 'bg-yellow-500'}`} />
                <span className="text-sm text-slate-600 dark:text-slate-300">TTS Worker</span>
                <span className="text-xs text-slate-400 dark:text-slate-500">{runningCount} jobs</span>
              </div>

              {/* Distribution Worker - NOT CONNECTED */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-slate-400" />
                <span className="text-sm text-red-500 opacity-50">Distribution</span>
                <span className="text-xs text-red-400 opacity-50">Not Connected</span>
              </div>

              {/* R2 Storage - NOT CONNECTED */}
              <div className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full bg-slate-400" />
                <span className="text-sm text-red-500 opacity-50">R2 Storage</span>
                <span className="text-xs text-red-400 opacity-50">--</span>
              </div>

              <Link to="/tts/health" className="text-xs text-primary-500 hover:text-primary-600 font-medium">
                View Details →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
